/**
 * Chat document albums.
 *
 * Sending several documents at once produces one `doc` message per file, so
 * without grouping a batch reads as N stacked bubbles. `groupConsecutiveDocs`
 * collapses those into a single bubble carrying a `documents` array — the
 * document counterpart of the image merge in `buildRenderableSections` — and
 * `buildDocViewerItems` turns that array into the payload
 * `FullScreenImageViewer` already understands.
 */
import { BASE_URL } from "../helper/constants";

export const MAX_DOCUMENTS = 10;

// The API rejects anything larger with a 413 (see helper/CrudFactory.js).
export const MAX_DOC_SIZE = 25 * 1024 * 1024;

const ALBUM_WINDOW_MINUTES = 1;

export const getMediaAlbumId = (msg) => {
  if (!msg) return "";
  if (msg.mediaAlbumId) return String(msg.mediaAlbumId);
  if (Array.isArray(msg.images) && msg.images.length > 0) {
    const fromImage = msg.images[0]?.mediaAlbumId;
    if (fromImage) return String(fromImage);
  }
  if (Array.isArray(msg.documents) && msg.documents.length > 0) {
    const fromDoc = msg.documents[msg.documents.length - 1]?.mediaAlbumId;
    if (fromDoc) return String(fromDoc);
  }
  return "";
};

/**
 * Files sent together share a mediaAlbumId and must stay in one gallery.
 * A later send gets a new id, so it is not absorbed into the previous album
 * even when it arrives within the legacy 1-minute window.
 * Messages with no id (history from before this field) still use that window.
 */
export const isSameMediaSend = (previous, current, previousTs, currentTs) => {
  const prevId = getMediaAlbumId(previous);
  const currId = getMediaAlbumId(current);
  if (prevId || currId) {
    return !!prevId && prevId === currId;
  }
  if (!previousTs || !currentTs) return false;
  return Math.abs(previousTs - currentTs) / 60000 <= ALBUM_WINDOW_MINUTES;
};

/**
 * Extensions the document picker accepts, shared by the file input and the
 * validation that follows. Same set the single-file upload already allowed.
 */
export const DOC_EXTENSIONS = [
  "doc",
  "docx",
  "odt",
  "odf",
  "txt",
  "pdf",
  "xls",
  "xlsx",
  "xlsb",
  "xlsm",
  "csv",
  "ppt",
  "pptx",
  "dwg",
  "dxf",
  "ifc",
  "mpp",
  "json",
  "zip",
];

export const DOC_ACCEPT_ATTRIBUTE = DOC_EXTENSIONS.map((ext) => `.${ext}`).join(",");

/**
 * Formats the Media-section DocViewer can actually render. CAD / project files
 * (dwg, dxf, ifc, mpp, …) stay download-only.
 */
export const PREVIEWABLE_DOC_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "txt",
  "xls",
  "xlsx",
  "csv",
  "ppt",
  "pptx",
];

const EXTENSION_ICONS = {
  pdf: "picture_as_pdf",
  doc: "description",
  docx: "description",
  odt: "description",
  odf: "description",
  rtf: "description",
  txt: "description",
  json: "data_object",
  xls: "table_chart",
  xlsx: "table_chart",
  xlsm: "table_chart",
  xlsb: "table_chart",
  ods: "table_chart",
  csv: "table_chart",
  ppt: "slideshow",
  pptx: "slideshow",
  odp: "slideshow",
  zip: "folder_zip",
  rar: "folder_zip",
  "7z": "folder_zip",
  tar: "folder_zip",
  gz: "folder_zip",
  mp3: "audio_file",
  wav: "audio_file",
  m4a: "audio_file",
  mp4: "video_file",
  mov: "video_file",
  avi: "video_file",
  mkv: "video_file",
  dwg: "architecture",
  dxf: "architecture",
  ifc: "architecture",
  mpp: "calendar_month",
};

const EXTENSION_COLORS = {
  pdf: "#d93025",
  doc: "#2b579a",
  docx: "#2b579a",
  odt: "#2b579a",
  odf: "#2b579a",
  rtf: "#2b579a",
  txt: "#5f6368",
  json: "#5f6368",
  xls: "#1d6f42",
  xlsx: "#1d6f42",
  xlsm: "#1d6f42",
  xlsb: "#1d6f42",
  ods: "#1d6f42",
  csv: "#1d6f42",
  ppt: "#d24726",
  pptx: "#d24726",
  odp: "#d24726",
  zip: "#8a6d3b",
  rar: "#8a6d3b",
  "7z": "#8a6d3b",
  tar: "#8a6d3b",
  gz: "#8a6d3b",
  mp3: "#6a1b9a",
  wav: "#6a1b9a",
  m4a: "#6a1b9a",
  mp4: "#00695c",
  mov: "#00695c",
  avi: "#00695c",
  mkv: "#00695c",
  dwg: "#455a64",
  dxf: "#455a64",
  ifc: "#455a64",
  mpp: "#7048a8",
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "heic", "heif"];

export const SPREADSHEET_EXTENSIONS = ["xls", "xlsx", "xlsm", "xlsb", "ods", "csv"];

export const getDocFileUrl = (doc) => {
  if (!doc) return "";
  const url = Array.isArray(doc.fileUrl) ? doc.fileUrl[0] : doc.fileUrl;
  return typeof url === "string" ? url : "";
};

export const getDocMessageName = (doc) => {
  if (!doc) return "document";
  if (doc.attachedFileName) return String(doc.attachedFileName).split("?")[0];
  if (doc.name) return String(doc.name).split("?")[0];
  const fromUrl = getDocFileUrl(doc).split("?")[0].split("/").pop();
  return fromUrl || "document";
};

export const getDocExtension = (doc) => {
  if (!doc) return "";
  // The file name is authoritative: server URLs are hashed and a data URL from
  // an optimistic send carries no extension at all.
  const source = doc.attachedFileName || doc.docName || doc.fileName || doc.name || getDocFileUrl(doc) || "";
  const cleaned = String(source).split("?")[0];
  const parts = cleaned.split(".");
  if (parts.length < 2) return "";
  return parts.pop().toLowerCase();
};

export const getDocKind = (doc) => {
  if (!doc) return "other";
  const extension = getDocExtension(doc);
  if (extension === "pdf") return "pdf";
  if (IMAGE_EXTENSIONS.includes(extension)) return "image";
  if (SPREADSHEET_EXTENSIONS.includes(extension)) return "spreadsheet";
  const mime = doc.type || doc.mime || "";
  if (typeof mime === "string") {
    if (mime === "application/pdf") return "pdf";
    if (mime.startsWith("image/")) return "image";
    if (
      mime === "application/vnd.ms-excel" ||
      mime === "text/csv" ||
      mime.includes("spreadsheet")
    ) {
      return "spreadsheet";
    }
  }
  return "other";
};

export const isSpreadsheetDoc = (doc) => {
  if (!doc) return false;
  if (doc.docKind === "spreadsheet") return true;
  return getDocKind(doc) === "spreadsheet";
};

/** Open in the shared full-screen viewer (spreadsheet/PDF/image, or anything
 *  the Media DocViewer cannot render). Word/PPT still use DocViewer. */
export const shouldOpenInlineDocViewer = (doc) => {
  const kind = getDocKind(doc);
  return kind === "spreadsheet" || kind === "pdf" || kind === "image" || !canPreviewDoc(doc);
};

/** True when the Media DocViewer can preview this posted document. */
export const canPreviewDoc = (doc) => {
  if (!PREVIEWABLE_DOC_EXTENSIONS.includes(getDocExtension(doc))) return false;
  const url = getDocFileUrl(doc);
  if (!url) return false;
  // Optimistic/local blobs can't be opened in DocViewer (it prefixes BASE_URL).
  if (url.startsWith("blob:") || url.startsWith("data:")) return false;
  return true;
};

export const getDocIconName = (doc) => {
  const kind = getDocKind(doc);
  if (kind === "image") return "image";
  return EXTENSION_ICONS[getDocExtension(doc)] || "draft";
};

export const getDocAccentColor = (doc) => {
  const kind = getDocKind(doc);
  if (kind === "image") return "#0b8043";
  return EXTENSION_COLORS[getDocExtension(doc)] || "#5f6368";
};

export const isDocOversized = (file) => (file?.size || 0) > MAX_DOC_SIZE;

export const formatDocSize = (bytes) => {
  const value = Number(bytes) || 0;
  if (value <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${parseFloat((value / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
};

/** Absolute URL for previewing or downloading a document message. */
export const resolveDocUrl = (doc) => {
  const url = getDocFileUrl(doc);
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) {
    return url;
  }
  return BASE_URL + url;
};

const hasCaption = (msg) => typeof msg?.message === "string" && msg.message.trim() !== "";

/**
 * Only plain, delivered documents join an album. Replies and deletions keep
 * their own bubble because they have dedicated renderers the album layout
 * would otherwise swallow.
 */
export const canJoinDocAlbum = (msg) =>
  !!msg &&
  msg.messageType === "doc" &&
  !msg.isDeleted &&
  !msg.isReply &&
  !msg.isMultipleDocs;

/**
 * Merge `current` into the previous bubble when they belong to the same batch,
 * mutating `grouped` in place. Returns true when it was merged.
 *
 * Captions are compared only by emptiness: a batch send puts the caption on
 * the last file, so an album may mix one captioned document with uncaptioned
 * ones, but two captioned documents are treated as two separate sends. Values
 * cannot be compared directly because the same text encrypts differently each
 * time.
 */
export const groupConsecutiveDocs = (grouped, current) => {
  if (!Array.isArray(grouped) || !canJoinDocAlbum(current)) return false;

  const album = grouped[grouped.length - 1];
  if (!album || !Array.isArray(album.documents) || album.senderId !== current.senderId) {
    return false;
  }
  if (hasCaption(album) && hasCaption(current)) return false;

  const previous = album.documents[album.documents.length - 1];
  const previousTs =
    previous?.createdAtTs || (previous?.createdAt ? new Date(previous.createdAt).getTime() : 0);
  const currentTs =
    current.createdAtTs || (current.createdAt ? new Date(current.createdAt).getTime() : 0);
  if (!isSameMediaSend(previous, current, previousTs, currentTs)) return false;

  const documents = [...album.documents, current];
  grouped[grouped.length - 1] = {
    ...album,
    // the caption may live on any file of the batch, so keep whichever exists
    message: hasCaption(album) ? album.message : current.message,
    documents,
    isMultipleDocs: true,
    docCount: documents.length,
    mediaAlbumId: album.mediaAlbumId || current.mediaAlbumId,
  };
  return true;
};

/**
 * Map album documents onto viewer slides. PDFs and images reuse the existing
 * iframe and zoom branches; anything else is flagged so the viewer can show a
 * typed "no preview" card.
 */
export const buildDocViewerItems = (documents) =>
  (documents || []).map((doc) => {
    const kind = getDocKind(doc);
    return {
      ...doc,
      isDoc: true,
      docKind: kind,
      isPdf: kind === "pdf",
      docName: getDocMessageName(doc),
      docUrl: resolveDocUrl(doc),
      docExtension: getDocExtension(doc),
      docIcon: getDocIconName(doc),
      docColor: getDocAccentColor(doc),
      docSize: doc?.fileSize || doc?.size || 0,
    };
  });
